/** SHA-256 hash for RSVP password storage (salt:password). */
export async function hashRsvpPassword(password: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}:${password}`);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function generateRsvpSalt(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function formatPublicRsvpUrl(token: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/rsvp/public/${token}`;
}

export interface PublicRsvpEventInfo {
  id: string;
  event_date: string;
  location: string | null;
  group_id: string;
  requires_password: boolean;
}

async function fetchEventByPollToken(
  token: string
): Promise<{
  id: string;
  event_date: string;
  location: string | null;
  group_id: string;
  rsvp_password_hash: string | null;
} | null> {
  const { supabase } = await import("@/lib/supabase");
  const { data: poll, error: pollError } = await supabase
    .from("polls")
    .select("id")
    .eq("poll_public_token", token)
    .maybeSingle();

  if (pollError) throw pollError;
  if (!poll?.id) return null;

  const { data, error } = await supabase
    .from("match_events")
    .select("id, event_date, location, group_id, rsvp_password_hash")
    .eq("poll_id", poll.id)
    .in("status", ["convocatoria", "collecting"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchPublicRsvpEvent(token: string): Promise<PublicRsvpEventInfo | null> {
  const { supabase } = await import("@/lib/supabase");
  const { data, error } = await supabase
    .from("match_events")
    .select("id, event_date, location, group_id, rsvp_password_hash")
    .eq("rsvp_public_token", token)
    .maybeSingle();

  if (error) throw error;

  const resolved = data ?? (await fetchEventByPollToken(token));
  if (!resolved) return null;

  return {
    id: resolved.id,
    event_date: resolved.event_date,
    location: resolved.location,
    group_id: resolved.group_id,
    requires_password: Boolean(resolved.rsvp_password_hash),
  };
}

export async function setupPublicRsvpPassword(
  eventId: string,
  password: string
): Promise<{ token: string; url: string }> {
  const { supabase } = await import("@/lib/supabase");
  const salt = generateRsvpSalt();
  const hash = `${salt}:${await hashRsvpPassword(password, salt)}`;

  const { data, error } = await supabase
    .from("match_events")
    .update({ rsvp_password_hash: hash })
    .eq("id", eventId)
    .select("rsvp_public_token")
    .single();

  if (error) throw error;
  const token = data.rsvp_public_token as string;
  return { token, url: formatPublicRsvpUrl(token) };
}

type PublicRsvpErrorCode =
  | "MISSING_FIELDS"
  | "MISSING_PLAYER"
  | "EVENT_NOT_FOUND"
  | "PASSWORD_NOT_SET"
  | "INVALID_PASSWORD"
  | "EVENT_CLOSED"
  | "EVENT_FULL"
  | "INTERNAL_ERROR"
  | "PUBLIC_RSVP_FAILED";

async function callPublicRsvpApi(body: Record<string, unknown>): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/public-rsvp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error((payload as { error?: PublicRsvpErrorCode }).error ?? "PUBLIC_RSVP_FAILED");
  }
}

export async function verifyPublicRsvpPassword(token: string, password: string): Promise<void> {
  await callPublicRsvpApi({ action: "verify", token, password });
}

export async function submitPublicRsvp(payload: {
  token: string;
  password: string;
  status: "going" | "maybe" | "out";
  playerId?: number;
  guestName?: string;
}): Promise<void> {
  await callPublicRsvpApi({ action: "submit", ...payload });
}
